import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { messageHelpGroups } from './events';

const copyFor = (t, entry) => ({
  title: t(`MessageControlHelp.When.${entry.key}.Title`, {
    defaultValue: entry.label,
  }),
  body: t(`MessageControlHelp.When.${entry.key}.Body`, {
    defaultValue: entry.when,
  }),
  methods: t(`MessageControlHelp.When.${entry.key}.Methods`, {
    defaultValue: entry.methods,
  }),
  channels: t(`MessageControlHelp.When.${entry.key}.Channels`, {
    defaultValue: entry.channels,
  }),
});

const matchesQuery = (entry, translated, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return [entry.key, translated.title, translated.body, translated.methods, translated.channels]
    .join(' ')
    .toLowerCase()
    .indexOf(q) !== -1;
};

const WhenCard = ({ t, entry, current }) => {
  const text = copyFor(t, entry);
  return (
    <article
      className={'mcc-when-card' + (current ? ' is-current' : '')}
    >
      <h3>{text.title}</h3>
      <p>{text.body}</p>
      <p className="mcc-when-meta">
        {t('MessageControlHelp.WhenUsedFor', { defaultValue: 'Used for' })}
        {': '}
        {text.methods}
        {' · '}
        {text.channels}
      </p>
    </article>
  );
};

export const MessageWhenHelpDialog = ({
  t,
  open,
  onClose,
  method,
  channel,
  eventKey,
}) => {
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);
  const groups = useMemo(
    () => messageHelpGroups({ method, channel }),
    [method, channel]
  );
  const patient = groups.patient.filter((entry) =>
    matchesQuery(entry, copyFor(t, entry), query)
  );
  const staff = groups.staff.filter((entry) =>
    matchesQuery(entry, copyFor(t, entry), query)
  );
  const empty = !patient.length && !staff.length;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      className="features-help-dialog"
      style={{ zIndex: 1600 }}
      PaperProps={{ className: 'features-help-paper' }}
    >
      <div className="features-help-hero">
        <IconButton
          aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
          onClick={onClose}
          className="features-help-close"
        >
          <CloseIcon />
        </IconButton>
        <p className="features-help-eyebrow">
          {t('MessageControlHelp.Eyebrow', { defaultValue: 'Studio' })}
        </p>
        <h2>
          {t('MessageControlHelp.WhenTitle', {
            defaultValue: 'When each message is sent',
          })}
        </h2>
        <p>
          {t('MessageControlHelp.WhenSubtitle', {
            defaultValue:
              'Each template is one notification. Publishing Ready does not change Reminder, Receipt, or any other message.',
          })}
        </p>
      </div>
      <DialogContent className="features-help-body">
        <TextField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('MessageControlHelp.WhenSearch', {
            defaultValue: 'Search messages…',
          })}
          size="small"
          fullWidth
          variant="outlined"
          className="mcc-when-search"
        />
        {channel === 'mobile' ? (
          <p className="mcc-helper">
            {t('MessageControlHelp.WhenLinkNote', {
              defaultValue:
                'The patient link page is only Ready, or Assigned / ready at bedside. The other messages are SMS or email.',
            })}
          </p>
        ) : null}
        {patient.length ? (
          <section className="mcc-when-group">
            {staff.length ? (
              <h3>
                {t('MessageControlHelp.WhenPatient', {
                  defaultValue: 'Patient messages',
                })}
              </h3>
            ) : null}
            {patient.map((entry) => (
              <WhenCard
                key={entry.key}
                t={t}
                entry={entry}
                current={entry.key === eventKey}
              />
            ))}
          </section>
        ) : null}
        {staff.length ? (
          <section className="mcc-when-group">
            <h3>
              {t('MessageControlHelp.WhenStaff', {
                defaultValue: 'Staff mail',
              })}
            </h3>
            {staff.map((entry) => (
              <WhenCard
                key={entry.key}
                t={t}
                entry={entry}
                current={entry.key === eventKey}
              />
            ))}
          </section>
        ) : null}
        {empty ? (
          <p className="mcc-helper">
            {t('MessageControlHelp.WhenEmpty', {
              defaultValue: 'No messages match that search.',
            })}
          </p>
        ) : null}
        <p className="mcc-when-custom">
          {t('MessageControlHelp.WhenCustom', {
            defaultValue:
              'A custom event is used only when a send path looks up that exact key. It does not replace Ready or any other message.',
          })}
        </p>
      </DialogContent>
      <DialogActions className="features-help-actions">
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('MessageControl.Close', { defaultValue: 'Close' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MessageWhenHelpButton = ({
  t,
  method,
  channel,
  eventKey,
  size = 'medium',
}) => {
  const [open, setOpen] = useState(false);
  const label = t('MessageControlHelp.WhenTooltip', {
    defaultValue: 'When each message is sent',
  });
  return (
    <React.Fragment>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          onClick={() => setOpen(true)}
          size={size}
          className="mcc-when-help-btn"
        >
          <HelpOutlineIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </Tooltip>
      <MessageWhenHelpDialog
        t={t}
        open={open}
        method={method}
        channel={channel}
        eventKey={eventKey}
        onClose={() => setOpen(false)}
      />
    </React.Fragment>
  );
};

export default MessageWhenHelpButton;



// WEBPACK FOOTER //
// ./src/components/message-control-center/MessageWhenHelp.js